import * as React from "react"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { Autocomplete, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close';
import Grid from "@mui/material/Grid2"
import { Button } from "@mui/material"
import { emailRegex } from "../../../Constants"
import { findLocation, useLocations } from "../../ReactQuery/QueryLocations"
import { formatCustomerName, formatPhoneNumber } from "../../../Utils"
import { MaterialUISwitchTextEmail } from "../../../Utils/MaterialUISwitch"
import { template_service_communication } from "../../../Utils/service"
import { MuiTelInput } from "mui-tel-input"

export default function SendQuoteInvoiceModal({
    open,
    closeModal,
    serviceOrder,
    title = "Send",
    defaultTextMessage = "",
    defaultEmailMessage = "",
    text_hint = "",
    show_text_character_count = true,
    extra_text_character_count = 0,
    email_hint = "",
}) {
    const { data: locations, } = useLocations();
    const [customerCommunicationType, setCustomerCommunicationType] = React.useState('text')
    const [customerCommunicationPhone, setCustomerCommunicationPhone] = React.useState('')
    const [customerCommunicationEmail, setCustomerCommunicationEmail] = React.useState('')
    const [customerCommunicationMessage, setCustomerCommunicationMessage] = React.useState('')
    const [phones, setPhones] = React.useState([])
    const [emails, setEmails] = React.useState([])

    const submit = (e) => {
        e.preventDefault();
        closeModal(customerCommunicationType, customerCommunicationType === 'email' ? customerCommunicationEmail.toLowerCase() : customerCommunicationPhone, customerCommunicationMessage)
    }

    const valid_email = Boolean(customerCommunicationEmail.toLowerCase().match(emailRegex))
    const valid_phone = /^\d{10}$/.test(customerCommunicationPhone)

    React.useEffect(() => {
        if (open) {
            const default_customer_communication_type = ((serviceOrder.preferred_phone && 'text') || (serviceOrder.preferred_email && 'email') || serviceOrder.customer.preferred_communication_type) ?? 'text'
            const service_order_location = findLocation(locations, serviceOrder.location_id);
            const new_message = (default_customer_communication_type === 'email' ? defaultEmailMessage : defaultTextMessage) ?? ''
            setCustomerCommunicationType(default_customer_communication_type)
            setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_message))
            setCustomerCommunicationPhone((serviceOrder.preferred_phone || serviceOrder.customer.phone) ?? '')
            setCustomerCommunicationEmail((serviceOrder.preferred_email || serviceOrder.customer.email) ?? '')
            setPhones((serviceOrder.preferred_phone ? [{ value: serviceOrder.preferred_phone, name: "Preferred Contact" }] : []).concat(
                serviceOrder.customer.phone ? [{ value: serviceOrder.customer.phone, name: formatCustomerName(serviceOrder.customer) }] : []
            ).concat(
                (serviceOrder.customer.customer_contacts ?? []).sort((a, b) => (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name)).map((contact) => contact.phone ? { value: contact.phone, name: `${contact.first_name} ${contact.last_name}` } : undefined).filter((x) => x !== undefined)
            ))
            setEmails((serviceOrder.preferred_email ? [{ value: serviceOrder.preferred_email, name: "Preferred Contact" }] : []).concat(
                serviceOrder.customer.email ? [{ value: serviceOrder.customer.email, name: formatCustomerName(serviceOrder.customer) }] : []
            ).concat(
                (serviceOrder.customer.customer_contacts ?? []).sort((a, b) => (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name)).map((contact) => contact.email ? { value: contact.email, name: `${contact.first_name} ${contact.last_name}` } : undefined).filter((x) => x !== undefined)
            ))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth='md'
                fullWidth
            >
                <form onSubmit={submit}>
                    <DialogTitle>
                        <span style={{ "paddingRight": "24px" }}>{title}</span>
                        <IconButton
                            aria-label="close"
                            onClick={() => closeModal(false)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container alignItems={"center"} justifyContent="space-between" spacing={2}>
                            <Grid size="auto">
                                <Stack direction="row" component="label" alignItems="center" justifyContent="flex-begin">
                                    <Typography sx={{ fontWeight: 'bold' }}>Text</Typography>
                                    <MaterialUISwitchTextEmail
                                        onChange={(e) => {
                                            const service_order_location = findLocation(locations, serviceOrder.location_id);
                                            const new_message = (e.target.checked ? defaultEmailMessage : defaultTextMessage) ?? ''
                                            setCustomerCommunicationType(e.target.checked ? 'email' : 'text');
                                            setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_message))
                                        }}
                                        checked={customerCommunicationType === 'email'}
                                    />
                                    <Typography sx={{ fontWeight: 'bold' }}>Email</Typography>
                                </Stack>
                            </Grid>
                            <Grid size="grow">
                                {customerCommunicationType === 'text' &&
                                    <Autocomplete
                                        options={phones}
                                        value={customerCommunicationPhone}
                                        isOptionEqualToValue={(option, value) => option.value === value}
                                        getOptionLabel={(option) => option.value ?? option}
                                        onChange={(event, newValue) => {
                                            setCustomerCommunicationPhone(newValue?.value ?? '');
                                        }}
                                        freeSolo
                                        size="small"
                                        sx={{
                                            '& .MuiFilledInput-root': {
                                                pt: 0,
                                                pl: '12px'
                                            },
                                            '& .MuiFilledInput-root .MuiFilledInput-input': {
                                                pt: '25px',
                                                pr: '12px',
                                                pb: '8px',
                                                pl: '12px',
                                            }
                                        }}
                                        renderInput={(params) => {
                                            return (
                                                <div ref={params.InputProps.ref}>
                                                    <MuiTelInput
                                                        {...params.inputProps}
                                                        ref={undefined}
                                                        inputRef={params.inputProps.ref}
                                                        className=""
                                                        margin="dense"
                                                        id="phone"
                                                        label="Phone"
                                                        type="tel"
                                                        fullWidth
                                                        variant="filled"
                                                        value={`+1 ${customerCommunicationPhone}`}
                                                        onChange={(event, extra) => {
                                                            if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
                                                                setCustomerCommunicationPhone(extra.nationalNumber)
                                                            }
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        onlyCountries={['US']}
                                                        defaultCountry="US"
                                                        forceCallingCode
                                                        disableDropdown
                                                    />
                                                </div>
                                            )
                                        }}
                                        renderOption={(props, option, { inputValue }) => {
                                            const { key, ...optionProps } = props;

                                            return (
                                                <li key={key} {...optionProps}>
                                                    <div>
                                                        <span>
                                                            {formatPhoneNumber(option.value)}
                                                        </span>
                                                        <br />
                                                        <Typography sx={{ opacity: '50%', fontSize: '.7rem', pl: 1 }}>{option.name}</Typography>
                                                    </div>
                                                </li>
                                            );
                                        }}
                                    />
                                }
                                {customerCommunicationType === 'email' &&
                                    <Autocomplete
                                        options={emails}
                                        value={customerCommunicationEmail}
                                        isOptionEqualToValue={(option, value) => option.value === value}
                                        getOptionLabel={(option) => option.value ?? option}
                                        onChange={(event, newValue) => {
                                            console.log(newValue)
                                            setCustomerCommunicationEmail(newValue?.value ?? '');
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setCustomerCommunicationEmail(newInputValue ?? '');
                                        }}
                                        freeSolo
                                        renderInput={(params) => {
                                            return (<TextField {...params} label="Email" variant="filled" type="email" fullWidth autoComplete='off' />)
                                        }}
                                        renderOption={(props, option, { inputValue }) => {
                                            const { key, ...optionProps } = props;

                                            return (
                                                <li key={option.value} {...optionProps}>
                                                    <div>
                                                        <span>
                                                            {option.value}
                                                        </span>
                                                        <br />
                                                        <Typography sx={{ opacity: '50%', fontSize: '.7rem', pl: 1 }}>{option.name}</Typography>
                                                    </div>
                                                </li>
                                            );
                                        }}
                                    />
                                }
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    margin="dense"
                                    id="quoteinvoicemessage"
                                    label="Message"
                                    type="text"
                                    fullWidth
                                    variant="filled"
                                    multiline
                                    minRows={2}
                                    value={customerCommunicationMessage}
                                    onChange={(event) => setCustomerCommunicationMessage(event.target.value)}
                                    helperText={customerCommunicationType === 'email' ?
                                        email_hint
                                        :
                                        `${text_hint ? text_hint + ' ' : ''}${show_text_character_count && `${customerCommunicationMessage.length + extra_text_character_count}/765`}`}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                            <Grid xs="auto">
                            </Grid>
                            <Grid xs="auto">
                                <Tooltip title={customerCommunicationType === 'email' ? !valid_email && 'Email is not valid' : !valid_phone && 'Phone is not valid'} arrow>
                                    <span> {/* span required for tooltip events */}
                                        <Button variant="contained" type="submit" disabled={!(customerCommunicationType === 'email' ? valid_email : valid_phone)}>
                                            Submit
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    )
}
